/* eslint-disable no-mixed-spaces-and-tabs */
import { call, fork, put, takeEvery, select } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../../../modules/notifications/actions';
import { FORM_NAME, FORM_FIELDS, REQUEST_ERROR_MESSAGE } from './constants';
import { DISMISS_TIME } from '../../../../api/constants';
import { errorHandler } from '../../../../api/utils';
import {
	getGroupsWithWaysListAction,
	getCollectionGroupListAction,
	getCollectionWayListAction,
	getWaysGroupListAction,
	setIsLoadingAction,
	getOrdersCollectionGroupListAction,
	getOrdersCollectionWayListAction,
	getGroupsListAction,
} from '../../actions';
import {
	getGroupOpenSelector,
	getGroupWayOpenSelector,
	getWayOpenSelector,
	getCollectionGroupOpenSelector,
	getCollectionWayOpenSelector,
} from '../../selectors';
import { transformToValidationErrors } from './utils';
import { getDataForCreateOrUpdateSelector, getFormIsInvalidCreateSelector } from './selectors';
import {
	getCategoriesRequest,
	getTenderCompaniesRequest,
	getTenderTypesRequest,
	getTypesDictionaryRequest,
	orderCreateReqRequest,
} from '../../../../api/requests';
import { getRequestsCountSaga } from '../../../reqs-list/saga';
import {
	GET_CATEGORIES,
	GET_TENDER_COMPANIES,
	GET_TENDER_TYPES,
	GET_TYPES,
	ORDER_CREATE_REQ,
	setCategoriesAction,
	setIsCategoriesLoadingAction,
	setIsTenderCompaniesLoadingAction,
	setIsTenderTypesLoading,
	setIsTypesLoadingAction,
	setTenderCompaniesAction,
	setTenderTypesAction,
	setTypesAction,
} from './actions';

export function* orderCreateReqSaga({
	payload: { searchQuery, orders, collection, group, group_ways, way, callback },
}) {
	try {
		yield put(setIsLoadingAction(true));

		const groupOpen = yield select(getGroupOpenSelector());

		const groupWayOpen = yield select(getGroupWayOpenSelector());

		const wayOpen = yield select(getWayOpenSelector());

		const collectionGroupOpen = yield select(getCollectionGroupOpenSelector());

		const collectionWayOpen = yield select(getCollectionWayOpenSelector());

		const isInvalid = yield select(getFormIsInvalidCreateSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForCreateOrUpdateSelector());

		const { data, message, toast, errors } = yield call(orderCreateReqRequest, {
			...dataForSave,
			orders,
			collection,
			group_id: group?.id,
			group_ways_id: group_ways?.id,
			way_id: way?.id,
		});

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getGroupsListAction({ searchQuery, noLoading: true }));
			yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));

			if (groupOpen) {
				yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery }));

				if (collectionGroupOpen) {
					yield put(
						getOrdersCollectionGroupListAction({
							row: groupOpen,
							collection: collectionGroupOpen,
							searchQuery,
						}),
					);
				}
			}

			if (wayOpen && (wayOpen.id !== way.id || !wayOpen.planning)) {
				yield put(getCollectionWayListAction({ row: wayOpen, searchQuery }));

				if (collectionWayOpen) {
					yield put(
						getOrdersCollectionWayListAction({
							row: wayOpen,
							collection: collectionWayOpen,
							searchQuery,
						}),
					);
				}
			}

			if (groupWayOpen) {
				yield put(getWaysGroupListAction({ row: groupWayOpen, searchQuery, noLoading: true }));
			}

			yield fork(getRequestsCountSaga);

			callback();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsLoadingAction(false));
	}
}

export function* getCategoriesSaga() {
	try {
		yield put(setIsCategoriesLoadingAction(true));

		const { data, message, toast } = yield call(getCategoriesRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setCategoriesAction(data.map(item => ({ key: item.id, value: item.id, text: item.name }))));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCategoriesLoadingAction(false));
	}
}

export function* getTenderTypesSaga() {
	try {
		yield put(setIsTenderTypesLoading(true));

		const { data, message, toast } = yield call(getTenderTypesRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setTenderTypesAction(data.map(item => ({ key: item.id, value: item.id, text: item.name }))));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTenderTypesLoading(false));
	}
}

export function* getTenderCompaniesSaga() {
	try {
		yield put(setIsTenderCompaniesLoadingAction(true));

		const { data, message, toast } = yield call(getTenderCompaniesRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setTenderCompaniesAction(data.map(item => ({ key: item.id, value: item.id, text: item.name }))));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTenderCompaniesLoadingAction(false));
	}
}

export function* getTypesSaga() {
	try {
		yield put(setIsTypesLoadingAction(true));

		const { data, message, toast } = yield call(getTypesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setTypesAction(data.map(item => ({ key: item.id, value: item.id, text: item.name }))));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTypesLoadingAction(false));
	}
}

export default function* orderGroupsCreateReqContainerSaga() {
	yield takeEvery(ORDER_CREATE_REQ, orderCreateReqSaga);
	yield takeEvery(GET_CATEGORIES, getCategoriesSaga);
	yield takeEvery(GET_TENDER_TYPES, getTenderTypesSaga);
	yield takeEvery(GET_TENDER_COMPANIES, getTenderCompaniesSaga);
	yield takeEvery(GET_TYPES, getTypesSaga);
}
